import { useTranslation } from 'react-i18next';
import React, { useEffect, useContext, useState, useRef, useMemo } from 'react';
import { NavContext } from '@/components/NavContext';
import { PageLayout } from '@/components/PageLayout';
import { Spinner, TEOLoader } from '@teo/components';
import { axios } from '@/axios';
import { config } from '@/config';
import { newUid, useStateAndRef, nthIndexOf } from '@/util';
import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { Contentcreators } from '@/models/Database';
import PublishModal from '@/pages/Protected/Create/PublishModal';
import { langData } from '@/mocks/landData';
import { LessonEdit } from '@/components/LessonEdit';
import { TranslatePublication } from '@/components/TranslatePublication';
import { ContentCreatorInviteModal } from '@/components/ContentCreatorInviteModal/ContentCreatorInviteModal';
import EditCourseHeader from './EditCourseHeader';
import { VersionPublication } from '@/components/VersionPublication';
import PublishCourse from '../PublishCourse/PublishCourse';
import { DuplicateLibrary } from '@/components/DuplicateLessons/DuplicateLibrary';
import NotificationMessage from '@/components/NotificationMessage/NotificationMessage';
import EditPublicationHomepage from './EditPublicationHomepage';
import { initMath } from '@/components/Froala/widgets/MathView';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Userwithorganisationwithidps } from '@/models';
import { NotificationContext } from '@/components/Contexts/NotificationContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import i18n from 'i18next';
import ChapterPage from '@/components/ChapterPage';
import { SidebarLessonTree } from '@/components/LessonTree/SidebarLessonTree';
import { SidebarScorm } from '@/components/LessonTree/SidebarScorm';
import SidebarExtraInfoTree from '@/components/ExtraInfo/SidebarExtraInfoTree';
import ShareModal from '@/pages/Protected/Create/ShareModal';
import { HintsContext } from '@/components/Contexts/HintsContext';
import { PageUrlContext } from '@/components/Contexts/PageUrlContext';
import ExportPublication from '@/components/ExportPublication';
import ScormLessonPage from '@/pages/Protected/Learn/Courses/ScormCourse/ScormLessonPage';
import ScormLessonContent from '@/pages/Protected/Learn/Courses/ScormCourse/ScormLessonContent';
import { SidebarXApi } from '@/components/LessonTree/SidebarXApi';
import XApiLessonContent from '../../Learn/Courses/XApiCourse/XApiLessonContent';
import { Information, Pencil } from '@teo/components/icons';
import { clsx } from 'clsx';

// NEW: Course language context
import { CourseLanguageContext } from '@/components/Contexts/CourseLanguageContext';
import Hint from '@/components/Hints/Hints';

const LOCK_TIMER = 2 * 60 * 1000;
const IDLE_TIMER = 15 * 60 * 1000;

function CreateCoursePage() {
  const { t } = useTranslation();
  const { setNavDisabled } = useContext(NavContext);
  const { newPage } = useContext(PageUrlContext);

  // NEW: local state that backs the CourseLanguageContext (always available)
  const [courseLang, setCourseLang] = useState<string>(i18n.language);

  const [isOpenPublish, setIsOpenPublish] = useState(false);
  const [publication, setPublication] = useState<any>();
  const [displayedUpdatedAt, setDisplayedUpdatedAt] = useState<
    string | undefined
  >();
  const touchUpdatedAt = () => setDisplayedUpdatedAt(new Date().toISOString());
  const [isOpenPublishSettings, setIsOpenPublishSettings] = useState(false);
  const { coursecontentId }: any = useParams();
  const [isOpenChangeLang, setIsOpenChangeLang] = useState(false);
  const [isOpenVersionModal, setIsOpenVersionModal] = useState(false);
  const [isOpenInviteModal, setIsOpenInviteModal] = useState(false);
  const [isOpenExportModal, setIsOpenExportModal] = useState(false);
  const [isOpenLibrary, setIsOpenLibrary] = useState(false);
  const { notification, setShowNotification } = useContext(NotificationContext);
  const [refresh, setRefresh] = useState<string>(newUid(20));
  const [locked, setLocked, lockedRef] = useStateAndRef(true);
  const [lockedOut, setLockedOut] = useState<boolean>(false);
  const [isOpenShareSettings, setIsOpenShareSettings] = useState(false);
  const [loadVersion, setLoadVersion] = useState<any>(true);
  const [selectVersion, setSelectVersion] = useState(null);
  const [publicationType, setPublicationType] = useState();
  const [publicationName, setPublicationName] = useState<any>(null);
  const [publicationImage, setPublicationImage] = useState<any>(null);
  const [refreshScorm, setRefreshScorm] = useState<string>(newUid(20));
  const [newLangData, setNewLangData] = useState<any>([]);
  const [activeNode, realSetActiveNode, activeNodeRef] = useStateAndRef(null);
  const [isRefreshPage, setRefreshPage] = useState(false);
  const [loadeContent, setLoadeContent] = useState<any>(false);
  const navigate = useNavigate();

  const { auth } = useContext(AuthContext);
  const authUser = auth?.user as Userwithorganisationwithidps;

  const [tree, setTree] = useState<any>(null);

  const [isFroalaLoaded, setIsFroalaLoaded] = useState(null);
  const { hints, setHints } = useContext(HintsContext);
  const [showWelcomeMessage, setShowWelcomeMessage] = useState(false);

  useGet(`/coursecontent/${coursecontentId}`, {
    retry: false,
    onSuccess: (res: any) => {
      if (res?.data?.masterId !== null) {
        setTimeout(() => {
          return navigate(`/${i18n.language}/noaccess`);
        }, 0);
      }
      setPublication(res?.data || undefined);
      setDisplayedUpdatedAt(res?.data?.updatedAt);
      setPublicationType(res?.data?.publicationCourseType || 'course');
      setPublicationName(res?.data?.name);
      setPublicationImage(res?.data?.image);

      // NEW: try to sync course language from API payload (support both keys)
      const detectedLang =
        res?.data?.lang ?? res?.data?.language ?? i18n.language;
      setCourseLang(String(detectedLang));
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[CourseLanguageContext] set from API:', detectedLang);
      }
    },
    onError: (res: any) => {
      setTimeout(() => {
        return navigate(`/${i18n.language}/noaccess`);
      }, 0);
    },
    refetch: refreshScorm,
  });

  const isNotTeo = !publication?.publicationType?.startsWith?.('teo');
  const isScorm = publication?.publicationType?.startsWith?.('scorm');
  const isXApi = publication?.publicationType?.startsWith?.('xapi');

  const idleTimer: any = useRef(null);
  const handleIdle = () => {
    clearInterval(lockTimer.current);
    clearTimeout(idleTimer.current);
    clearInterval(autosaveTimer.current);
    const lessonId = activeNodeRef?.current?.data?.lessonId;
    axios.delete(config.backend + '/lessonlock/' + lessonId);
    setLocked(false);
  };

  const detectIdle = async (ev: any) => {
    if (document.visibilityState !== 'visible') {
      idleTimer.current = setTimeout(() => {
        clearTimeout(idleTimer.current);
        handleIdle();
      }, IDLE_TIMER);
    } else {
      const lessonId = activeNodeRef?.current?.data?.lessonId;
      clearTimeout(idleTimer.current);
      if (lessonId && !lockedRef.current) {
        localStorage.setItem('lessonlock', lessonId);
        await axios
          .post(config.backend + '/lessonlocks', {
            lessonId,
            userId: authUser?.id,
          })
          .catch((e) => {
            setLockedOut(true);
            setActiveNode(null);
          });
        autosaveTimerReset.current();
        lockTimerReset.current();
        setLocked(true);
        setRefresh(newUid(20));
      }
    }
  };

  useEffect(() => {
    const lessonId = activeNode?.data?.lessonId;
    if (lessonId) {
      document.addEventListener('visibilitychange', detectIdle, false);
    } else {
      document.removeEventListener('visibilitychange', detectIdle, false);
    }
    return () => {
      clearTimeout(idleTimer.current);
      document.removeEventListener('visibilitychange', detectIdle, false);
    };
  }, [activeNode]);

  useEffect(() => {
    const sortLangData = langData
      ?.map((el) => ({
        ...el,
        name: t(`langs.${el?.value}`),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    setNewLangData(sortLangData);
  }, [langData, t]);

  const lockTimer: any = useRef(null);
  const lockTimerReset: any = useRef(null);
  useEffect(() => {
    initMath();
    lockTimerReset.current = () => {
      clearInterval(lockTimer.current);
      lockTimer.current = setInterval(async () => {
        const lessonId = activeNodeRef?.current?.data?.lessonId;
        if (lessonId && authUser?.id) {
          localStorage.setItem('lessonlock', lessonId);
          axios.put(config.backend + '/lessonlock/' + lessonId, {
            lessonId,
            userId: authUser?.id,
          });
        }
      }, LOCK_TIMER - 30 * 1000);
    };
    lockTimerReset.current();
    return () => {
      const lessonId = activeNodeRef?.current?.data?.lessonId;
      if (lessonId && lockedRef.current) {
        axios.delete(config.backend + '/lessonlock/' + lessonId);
        setLocked(false);
      }
      clearInterval(lockTimer.current);
    };
  }, []);

  const setActiveNode = async (node: any) => {
    saveFn && (await saveFn());
    const newLessonId = node?.data?.id;
    const oldLessonId = activeNode?.data?.id;

    if (oldLessonId === newLessonId) return;

    const enter = () => {
      //we have permission to enter
      if (activeNodeRef?.current?.data?.lessonId && authUser?.id) {
        //release previous lock if any
        const lessonId = activeNodeRef?.current?.data?.lessonId;
        if (lessonId && lockedRef.current) {
          axios.delete(config.backend + '/lessonlock/' + lessonId);
          setLocked(false);
        }
      }
      if (node?.data?.lessonId && authUser?.id) {
        //acquire new lock if any
        if (node?.data?.canEdit === true) {
          localStorage.setItem('lessonlock', node.data.lessonId);
          axios
            .post(config.backend + '/lessonlocks', {
              lessonId: node.data.lessonId,
              userId: authUser?.id,
            })
            .catch((error) => {
              if (!navigator.onLine || error.message === 'Network Error') {
                alert('No internet connection');
                window.location.reload();
              } else {
                console.error('Error during request:', error);
              }
            });
          setLocked(true);
          lockTimerReset.current(); //refresh the lock
        }
      } else {
        clearInterval(lockTimer.current); //cancel the lock referesh as we have no lock
      }
      realSetActiveNode(node);
    };

    if (!node?.data?.lessonId) {
      return enter();
    } //no need to acquire lock as we're not entering a lesson
    axios
      .get(config.backend + '/lessonlock/' + node?.data?.lessonId)
      .then(() => {
        setShowNotification?.(t('notifications.lesson_locked'));
        return;
      })
      .catch(() => {
        enter();
      });
  };

  const [prevTree, setPrevTree] = useState<any[]>([]);
  const [addLesson, setAddLesson] = useState<any>();
  const [addFolder, setAddFolder] = useState<any>();
  const [directories, setDirectories] = useState<any>(null);
  const [lessons, setLessons] = useState<any>(null);
  const queryParams = new URLSearchParams(window.location.search);
  const [lesQueryParams, setLesQueryParams] = useState<any>(
    queryParams.get('les')
  );

  const [saveFn, setSaveFn, saveFnRef] = useStateAndRef();

  const [mobileSidebar, setMobileSidebar] = useState(true);

  const [parentPath, setParentPath] = useState<any>(null);
  const [parentId, setParentId] = useState<any>(null);

  const [extraInfoEdit, setExtraInfoEdit] = useState(false);
  const [parentNode, setParentNode] = useState(null);
  const [updateLesInfo, setUpdateLesInfo] = useState(newUid(20));
  const [contentCreators, setContentCreators] = useState<any>(null);
  const [isOwner, setIsOwner] = useState<any>(false);

  const [isPiblished, setIsPiblished] = useState(false);

  useEffect(() => {
    setActiveNode(null);
  }, [refreshScorm]);

  const { refetch: refetchCreatorResults }: any = useGet<
    Array<Contentcreators>
  >(`/contentcreators`, {
    coursecontentId,
    onSuccess: (result: any) => {
      setContentCreators(result?.data || null);
    },
  });

  useEffect(() => {
    if (contentCreators) {
      const myUser = contentCreators?.find(
        (user: any) => user?.id === authUser?.id
      );
      if (
        (myUser && myUser?.role >= 90) ||
        (authUser?.activeOrganisationRole || 0) >= 90
      ) {
        setIsOwner(true);
      } else {
        setIsOwner(false);
      }
    }
  }, [contentCreators, authUser?.activeOrganisationRole]);

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  const isDesctop = useMediaQuery({
    query: '(max-width: 1023px)',
  });

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  }, [setNavDisabled]);

  useEffect(() => {
    if (
      isOpenPublishSettings ||
      isOpenChangeLang ||
      isOpenVersionModal ||
      isOpenInviteModal ||
      isOpenPublish ||
      isOpenLibrary ||
      isOpenShareSettings
    ) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [
    isOpenPublishSettings,
    isOpenChangeLang,
    isOpenVersionModal,
    isOpenInviteModal,
    isOpenPublish,
    isOpenLibrary,
    isOpenShareSettings,
  ]);

  useEffect(() => {
    if (prevTree && !activeNode) {
      if (prevTree?.length > 0) {
        const queryParams = new URLSearchParams(window.location.search);
        const activeLes: any = queryParams.get('les');
        setActiveNode(
          activeLes != 0
            ? prevTree?.find((el: any) => el?.data?.id == activeLes)
            : prevTree?.find((el: any) => el.lessonId)
        );

        queryParams.delete('les');
        const query = queryParams.toString();
        window.history.replaceState(
          {},
          '',
          query
            ? `${window.location.pathname}?${query}`
            : window.location.pathname
        );
      }
    }
  }, [prevTree, activeNode]);

  const handleScroll = () => {
    const scrollSection = document.getElementById('course-lessons');
    const footer = document.getElementById('footer_page');
    const scrollY = window.scrollY;
    if (scrollSection) {
      if (scrollY < 81) {
        const heightTop = 80 - scrollY;
        scrollSection.style.maxHeight = `calc(100vh - ${heightTop}px)`;
      } else {
        scrollSection.style.maxHeight = `calc(100vh-100px)`;
      }
    }
    if (footer) {
      const rect = footer.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const isVisible = rect.top < windowHeight && rect.bottom > 0;

      let visible = 0;
      if (isVisible && scrollSection) {
        const visibleTop = Math.max(rect.top, 0);
        const visibleBottom = Math.min(rect.bottom, windowHeight);
        visible = visibleBottom - visibleTop;
        if (scrollY > 80) {
          scrollSection.style.maxHeight = `calc(100vh - ${visible - 1}px)`;
        } else {
          scrollSection.style.maxHeight = `calc(100vh - ${
            visible - 1 + 80 - scrollY
          }px)`;
        }
      }
    }
  };

  useEffect(() => {
    if (hints) {
      const lastHint = hints?.allHints?.[hints?.allHints?.length - 1];
      if (lastHint == 0) {
        setShowWelcomeMessage(true);
      }
    }

    setPublicationType(publication?.publicationCourseType || 'course');
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [publication, hints]);

  // autosave setup
  const autosaveTimer: any = useRef(null);
  const autosaveTimerReset: any = useRef(null);
  useEffect(() => {
    initMath();
    autosaveTimerReset.current = () => {
      clearInterval(autosaveTimer.current);
      autosaveTimer.current = setInterval(async () => {
        if (saveFnRef.current) {
          saveFnRef.current(true);
        }
      }, 2 * 60 * 1000);
    };
    autosaveTimerReset.current();
    return () => {
      clearInterval(autosaveTimer.current);
    };
  }, []);

  const handleOpenLibrary = (path: any, id: any) => {
    setIsOpenLibrary(true);
    setParentPath(path);
    setParentId(id);
  };

  useEffect(() => {
    const redirectPath = window.location.pathname.slice(
      0,
      nthIndexOf(window.location.pathname, '/', 3)
    );
    if (isPiblished) {
      navigate(redirectPath);
    }
  }, [isPiblished, navigate]);

  // Memo provider value to avoid extra renders
  const courseLangValue = useMemo(
    () => ({ courseLang, setCourseLang }),
    [courseLang]
  );

  return (
    <CourseLanguageContext.Provider value={courseLangValue}>
      <div className="flex flex-col">
        {/* If publication not yet loaded, keep provider mounted and show a lightweight loader */}
        {!publication && (
          <div className="flex min-h-[40vh] items-center justify-center">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        )}

        {!!publication && (
          <>
            {!loadVersion && (
              <div className="fixed z-[201] m-auto flex min-h-screen min-w-full bg-white/75 p-10">
                <div className="m-auto">
                  <TEOLoader
                    loaderSrc="/images/TEOloader.gif"
                    className="w-[110px]"
                  />
                </div>
              </div>
            )}
            {lockedOut && (
              <ConfirmModal
                open
                title="pages.create_courses.locked_out_title"
                description="pages.create_courses.locked_out_description"
                onConfirm={() => setLockedOut(false)}
              />
            )}
            <EditCourseHeader
              publication={publication}
              setPublication={setPublication}
              displayedUpdatedAt={displayedUpdatedAt}
              touchUpdatedAt={touchUpdatedAt}
              setIsOpenChangeLang={setIsOpenChangeLang}
              setIsOpenInviteModal={setIsOpenInviteModal}
              setIsOpenVersionModal={setIsOpenVersionModal}
              contentCreators={contentCreators}
              setIsOpenPublish={setIsOpenPublish}
              setIsOpenPublishSettings={setIsOpenPublishSettings}
              setIsOpenShareSettings={setIsOpenShareSettings}
              setIsOpenExportModal={setIsOpenExportModal}
              setSelectVersion={setSelectVersion}
              activeNode={activeNode}
              setActiveNode={setActiveNode}
              coursecontentId={coursecontentId}
              setLesQueryParams={setLesQueryParams}
              setMobileSidebar={setMobileSidebar}
              mobileSidebar={mobileSidebar}
              newPage={newPage}
              isOwner={isOwner}
              onNavigate={async () => {
                try {
                  if (saveFn) {
                    await saveFn();
                  }
                } catch (e) {
                  console.error(e);
                }
              }}
            />
            {notification && <NotificationMessage />}

            <PageLayout>
              <div
                style={{
                  minHeight: 'calc(100vh - 150px)',
                  // maxHeight: isNotTeo ? 'calc(100vh - 150px)' : undefined,
                }}
                className={
                  isNotTeo
                    ? `max-h-[calc(100vh - 150px)] relative border-r border-solid border-grey-02`
                    : '' +
                      `min-h-[calc(100vh - 150px)] relative border-r border-solid border-grey-02 !p-0 md:max-w-[390px] ${
                        !isMobile || (mobileSidebar && isMobile)
                          ? ''
                          : ' hidden '
                      } `
                }
              >
                {!loadeContent && (
                  <div className="absolute inset-0 z-[11] flex bg-white/75">
                    <div className="m-auto p-10">
                      <TEOLoader
                        loaderSrc="/images/TEOloader.gif"
                        className="w-[100px]"
                      />
                    </div>
                  </div>
                )}
                <p
                  className={`px-5 pt-5 pb-2 text-base font-medium text-[#6d6d72] ${
                    extraInfoEdit && 'hidden'
                  }`}
                >
                  {t(
                    `pages.create_edit_course.sidebar_title_${publicationType}`
                  )}
                </p>
                <div
                  onClick={() => {
                    setMobileSidebar(false);
                    setActiveNode(null);
                    setLesQueryParams(null);
                  }}
                  className={clsx(
                    ` relative flex cursor-pointer items-center justify-between rounded-md px-5`,
                    !activeNode && 'bg-[#f8f8f9]',
                    hints?.id == 'hint_39' &&
                      'active-hint-create-page active-hint-parent active-hint-main-menu',
                    extraInfoEdit && 'hidden'
                  )}
                >
                  <span className="flex items-center pl-3">
                    <Hint
                      key={`hint_39_${newPage}`}
                      id={'hint_39'}
                      leftDots={'left-0 top-2'}
                      title={t('hint.title_39')}
                      order={39}
                      style={{
                        left: 'calc(100% + 20px)',
                        top: '0px',
                      }}
                    />
                    <Information className="m-2 w-6 min-w-6 text-grey-07" />
                    <p className="text-sm font-normal text-[#231f20]">
                      {t(`pages.create_edit_course.sidebar_title_home`)}
                    </p>
                  </span>
                  {!activeNode && (
                    <Pencil className="mx-2 w-5 min-w-5 text-[#fdbe06]" />
                  )}
                </div>
                <div
                  id="course-lessons"
                  className={`${
                    !extraInfoEdit && 'px-5 pb-5'
                  } sticky top-0 overflow-y-auto  md:w-[340px] lg:w-[390px] 
            ${
              (hints?.id == 'hint_39' || hints?.id == 'hint_40') &&
              'active-hints-tree z-[101] !overflow-y-visible bg-white'
            }
            `}
                >
                  <>
                    <div
                      className={`${extraInfoEdit && 'hidden'}`}
                      id="course-lessons-list"
                    >
                      {isScorm && (
                        <SidebarScorm
                          key={
                            coursecontentId + '_' + publication?.publicationPath
                          }
                          coursecontentId={coursecontentId}
                          publicationType={publicationType}
                          publicationPath={publication?.publicationPath}
                          activeNode={activeNode}
                          setActiveNode={realSetActiveNode}
                          isDesctop={isDesctop}
                          setLoadeContent={setLoadeContent}
                          loadeContent={loadeContent}
                        />
                      )}
                      {isXApi && (
                        <SidebarXApi
                          key={
                            coursecontentId + '_' + publication?.publicationPath
                          }
                          coursecontentId={coursecontentId}
                          publicationType={publicationType}
                          publicationPath={publication?.publicationPath}
                          activeNode={activeNode}
                          setActiveNode={realSetActiveNode}
                          isDesctop={isDesctop}
                          setLoadeContent={setLoadeContent}
                          loadeContent={loadeContent}
                        />
                      )}

                      {publication?.publicationType == 'teo' && (
                        <SidebarLessonTree
                          authUser={authUser}
                          isDesctop={isDesctop}
                          setMobileSidebar={setMobileSidebar}
                          setActiveNode={setActiveNode}
                          setLesQueryParams={setLesQueryParams}
                          activeNode={activeNode}
                          coursecontentId={coursecontentId}
                          setPrevTree={setPrevTree}
                          addLesson={addLesson}
                          setAddLesson={setAddLesson}
                          addFolder={addFolder}
                          setAddFolder={setAddFolder}
                          isRefreshPage={isRefreshPage}
                          setTree={setTree}
                          tree={tree}
                          prevTree={prevTree}
                          handleOpenLibrary={handleOpenLibrary}
                          setDirectories={setDirectories}
                          directories={directories}
                          setLessons={setLessons}
                          lessons={lessons}
                          setUpdateLesInfo={setUpdateLesInfo}
                          setLoadVersion={setLoadVersion}
                          loadVersion={loadVersion}
                          isFroalaLoaded={isFroalaLoaded}
                          hints={hints}
                          newPage={newPage}
                          publicationType={publicationType}
                          setLoadeContent={setLoadeContent}
                          loadeContent={loadeContent}
                          courseLang={courseLang}
                        />
                      )}
                    </div>

                    {extraInfoEdit && (
                      <SidebarExtraInfoTree
                        node={parentNode}
                        publication={publication}
                        activeNode={activeNode}
                        prevTree={prevTree}
                        setTree={setTree}
                        setAddLesson={setAddLesson}
                        setLessons={setLessons}
                        lessons={lessons}
                        setActiveNode={setActiveNode}
                        setParentNode={setParentNode}
                        setMobileSidebar={setMobileSidebar}
                        setExtraInfoEdit={setExtraInfoEdit}
                        extraInfoEdit={extraInfoEdit}
                        publicationName={publication?.name}
                        isFroalaLoaded={isFroalaLoaded}
                      />
                    )}
                  </>
                </div>
              </div>
              <div
                style={{
                  minHeight: 'calc(100vh - 150px)',
                  // maxHeight: isScorm ? 'calc(100vh - 150px)' : undefined,
                  width: '100%',
                }}
              >
                {isScorm && activeNode && (
                  <ScormLessonContent
                    width="calc(-350px + 100vw)"
                    coursecontentId={coursecontentId}
                    publicationPath={publication?.publicationPath}
                    autoInit={true}
                    node={activeNode}
                    itemId={activeNode['@_identifier']}
                    notHidden={true}
                  />
                )}
                {isXApi && activeNode && (
                  <XApiLessonContent
                    onExit={() => realSetActiveNode(null)}
                    width="calc(-350px + 100vw)"
                    coursecontentId={coursecontentId}
                    publicationPath={publication?.publicationPath}
                    autoInit={true}
                    node={activeNode}
                    itemId={activeNode['@_identifier']}
                    notHidden={true}
                  />
                )}
                {activeNode?.data?.id &&
                  (!mobileSidebar || !isMobile) &&
                  (activeNode?.droppable ? (
                    <div className="w-full">
                      <ChapterPage
                        node={activeNode}
                        publicationName={publication?.name}
                        publication={publication}
                        prevTree={prevTree}
                        setDirectories={setDirectories}
                        setTree={setTree}
                        setAddLesson={setAddLesson}
                        setLessons={setLessons}
                        lessons={lessons}
                        setActiveNode={setActiveNode}
                        setParentNode={setParentNode}
                        setMobileSidebar={setMobileSidebar}
                        setExtraInfoEdit={setExtraInfoEdit}
                        extraInfoEdit={extraInfoEdit}
                        updateLesInfo={updateLesInfo}
                        isFroalaLoaded={isFroalaLoaded}
                        hints={hints}
                        newPage={newPage}
                      />
                    </div>
                  ) : (
                    <div
                      className={`w-full pb-2 ${
                        !activeNode?.data?.canEdit &&
                        activeNode?.data?.lessonId &&
                        'p-5'
                      }`}
                    >
                      {
                        <LessonEdit
                          node={activeNode}
                          key={refresh}
                          setSaveFn={(fn: any) => {
                            setSaveFn(() => fn);
                          }}
                          resetAutoSave={() => autosaveTimerReset.current()}
                          publicationName={publication?.name}
                          publication={publication}
                          setTree={setTree}
                          prevTree={prevTree}
                          setAddLesson={setAddLesson}
                          setLessons={setLessons}
                          lessons={lessons}
                          directories={directories}
                          setActiveNode={setActiveNode}
                          setParentNode={setParentNode}
                          setMobileSidebar={setMobileSidebar}
                          setExtraInfoEdit={setExtraInfoEdit}
                          extraInfoEdit={extraInfoEdit}
                          updateLesInfo={updateLesInfo}
                          setIsFroalaLoaded={setIsFroalaLoaded}
                          isFroalaLoaded={isFroalaLoaded}
                          hints={hints}
                          newPage={newPage}
                          touchUpdatedAt={touchUpdatedAt}
                        ></LessonEdit>
                      }
                    </div>
                  ))}
                {!activeNode &&
                  publication?.id &&
                  !lesQueryParams &&
                  (!mobileSidebar || !isMobile) && (
                    <EditPublicationHomepage
                      publication={publication}
                      setRefreshScorm={setRefreshScorm}
                      coursecontentId={publication?.id}
                      currentPublication={publication}
                      setPublication={setPublication}
                      directories={directories}
                      setTree={setTree}
                      setAddLesson={setAddLesson}
                      setLessons={setLessons}
                      lessons={lessons}
                      prevTree={prevTree}
                      setActiveNode={setActiveNode}
                      setParentNode={setParentNode}
                      setMobileSidebar={setMobileSidebar}
                      setExtraInfoEdit={setExtraInfoEdit}
                      extraInfoEdit={extraInfoEdit}
                      isFroalaLoaded={isFroalaLoaded}
                      hints={hints}
                      setHints={setHints}
                      showWelcomeMessage={showWelcomeMessage}
                      setShowWelcomeMessage={setShowWelcomeMessage}
                      newPage={newPage}
                      publicationType={publicationType}
                      isOwner={isOwner}
                      setLoadeContent={setLoadeContent}
                    />
                  )}
              </div>
            </PageLayout>

            {isOpenPublish && (
              <PublishCourse
                publication={publication}
                data={newLangData}
                setIsOpenPublish={setIsOpenPublish}
                setIsPiblished={setIsPiblished}
                publicationType={publicationType}
                contentCreators={contentCreators}
                publicationName={publicationName}
                publicationImage={publicationImage}
              />
            )}

            {isOpenPublishSettings && (
              <PublishModal
                isOpenPublishSettings={isOpenPublishSettings}
                closeModal={() => setIsOpenPublishSettings(false)}
                publication={publication}
                data={newLangData}
                setIsOpenPublishSettings={setIsOpenPublishSettings}
                setPublication={setPublication}
                publicationType={publicationType}
              />
            )}
            {isOpenShareSettings && (
              <ShareModal
                isOpenShareSettings={isOpenShareSettings}
                closeModal={() => setIsOpenShareSettings(false)}
                publication={publication}
                data={langData}
                setIsOpenShareSettings={setIsOpenShareSettings}
                setPublication={setPublication}
                publicationType={publicationType}
              />
            )}
            {isOpenInviteModal && isOwner && (
              <ContentCreatorInviteModal
                onClose={() => setIsOpenInviteModal(false)}
                publication={publication}
                refetchCreatorResults={refetchCreatorResults}
                setPublication={setPublication}
                contentCreators={contentCreators}
                publicationType={publicationType}
                onNavigate={async () => {
                  try {
                    if (saveFn) {
                      await saveFn();
                    }
                  } catch (e) {
                    console.error(e);
                  }
                }}
              />
            )}

            {isOpenVersionModal && (
              <VersionPublication
                closeModal={() => setIsOpenVersionModal(false)}
                coursecontentId={coursecontentId}
                publication={publication}
                setPublication={setPublication}
                setIsOpenVersionModal={setIsOpenVersionModal}
                activeNode={activeNode}
                setLoadVersion={setLoadVersion}
                loadVersion={loadVersion}
                setIsOpenExportModal={setIsOpenExportModal}
                setSelectVersion={setSelectVersion}
                publicationType={publicationType}
              />
            )}

            {isOpenChangeLang && (
              <TranslatePublication
                onClose={() => setIsOpenChangeLang(false)}
                publication={publication}
                setRefreshPage={setRefreshPage}
                publicationType={publicationType}
              />
            )}
            {isOpenLibrary && (
              <DuplicateLibrary
                closeModal={() => setIsOpenLibrary(false)}
                coursecontentId={coursecontentId}
                prevTree={prevTree}
                setAddLesson={setAddLesson}
                setAddFolder={setAddFolder}
                setIsOpenLibrary={setIsOpenLibrary}
                parentPath={parentPath}
                parentId={parentId}
                setTree={setTree}
                publication={publication}
              />
            )}
            {isOpenExportModal && (
              <ExportPublication
                closeModal={() => {
                  setSelectVersion(null);
                  setIsOpenExportModal(false);
                }}
                setIsOpenExport={setIsOpenExportModal}
                setSelectVersion={setSelectVersion}
                publication={publication}
                user={authUser}
                title={t('pages.create_edit_course.export')}
                version={selectVersion}
              />
            )}
          </>
        )}
      </div>
    </CourseLanguageContext.Provider>
  );
}

export default CreateCoursePage;
